/**
 * Componentes visuais do Comercial, portados do protótipo aprovado
 * (DEMO-03, frontend/prototipo/src/componentes.tsx, CampoBusca.tsx e
 * ResumoIndicadores.tsx) sem redesenho: Abas, Modal acessível, selos, nota,
 * busca e o componente ÚNICO de resumo/KPI. Mais os estados das telas
 * persistentes (carregando, erro com "tentar de novo", vazio).
 */
import { Fragment, useCallback, useEffect, useId, useRef, useState, type PointerEvent as EventoPonteiro, type ReactNode } from 'react';
import { Icone, type NomeIcone } from '../componentes/icones.tsx';
import { ErroApi } from '../api.ts';

/* ---------- Selos ---------- */
export type Tom = 'aberto' | 'positivo' | 'parcial' | 'encerrado' | 'backlog' | 'alerta';
/** Selo de SITUAÇÃO: retângulo preenchido + texto (a cor nunca é o único meio). */
export function SeloSituacao({ texto, tom }: { texto: string; tom: Tom }) {
  // texto com partes "A · B · CON-000012": cada parte fica inteira (a quebra de linha só acontece entre partes) e o texto visível não muda
  const partes = texto.split(' · ');
  return (
    <span className={`selo-situacao tom-${tom}${partes.length > 1 ? ' selo-partes' : ''}`} data-teste="selo-situacao">
      {partes.length > 1
        ? partes.map((p, i) => (
            <Fragment key={i}>
              {i > 0 && ' · '}
              <span className="selo-parte">
                <ComCodigos texto={p} />
              </span>
            </Fragment>
          ))
        : <ComCodigos texto={texto} />}
    </span>
  );
}

/* ---------- Códigos (CON-000012, PGT-, MOV-, OCF-000011-ES…): nunca quebram no hífen ---------- */
const RE_CODIGO = /\b[A-Z]{2,4}-\d+(?:-[A-Z0-9]+)*\b/g;
/** Código isolado, sem quebra de linha. */
export function Codigo({ children }: { children: ReactNode }) {
  return <span className="codigo">{children}</span>;
}
/** Texto em partes "Banco TESTE · 0001 · 12345-6": parte sem espaço (agência, conta, código) nunca quebra; o texto visível não muda. */
export function TextoPartes({ texto }: { texto: string | null | undefined }) {
  if (!texto) return <>{texto ?? ''}</>;
  return (
    <>
      {texto.split(' · ').map((p, i) => (
        <Fragment key={i}>
          {i > 0 && ' · '}
          {/\s/.test(p) || /^[A-Z]{2,4}-\d+/.test(p) ? <ComCodigos texto={p} /> : <span className="sem-quebra">{p}</span>}
        </Fragment>
      ))}
    </>
  );
}
/** Texto livre com os códigos envolvidos em span.codigo (o texto visível não muda). */
export function ComCodigos({ texto }: { texto: string | null | undefined }) {
  if (!texto) return <>{texto ?? ''}</>;
  const pedacos: ReactNode[] = [];
  let ultimo = 0;
  for (const m of texto.matchAll(RE_CODIGO)) {
    const i = m.index ?? 0;
    if (i > ultimo) pedacos.push(texto.slice(ultimo, i));
    pedacos.push(<Codigo key={i}>{m[0]}</Codigo>);
    ultimo = i + m[0].length;
  }
  if (ultimo === 0) return <>{texto}</>;
  if (ultimo < texto.length) pedacos.push(texto.slice(ultimo));
  return <>{pedacos}</>;
}

/* ---------- Modal acessível (foco preso, Esc fecha, foco volta ao fechar) ---------- */
// pilha de modais abertos: Esc e Tab valem só para o de cima (um submodal não fecha nem rouba o foco do que está por baixo)
const pilhaModais: object[] = [];
export function Modal({ titulo, aoFechar, children, rodape, largo, medio, sobreposto }: { titulo: string; aoFechar: () => void; children: ReactNode; rodape: ReactNode; largo?: boolean; medio?: boolean; sobreposto?: boolean }) {
  const ref = useRef<HTMLDivElement>(null);
  const eu = useRef({}).current;
  // entra na pilha ao abrir e sai ao fechar (independe de re-renderizações do modal de baixo)
  useEffect(() => {
    pilhaModais.push(eu);
    return () => void pilhaModais.splice(pilhaModais.indexOf(eu), 1);
  }, [eu]);
  useEffect(() => {
    const anterior = document.activeElement as HTMLElement | null;
    // foco inicial no primeiro campo; o "?" de ajuda nunca recebe o foco inicial (abriria a dica e prenderia o Esc)
    const primeiro = ref.current?.querySelector<HTMLElement>('input:not([disabled]), select:not([disabled]), textarea:not([disabled])') ?? ref.current?.querySelector<HTMLElement>('button:not(.botao-ajuda)');
    if (pilhaModais[pilhaModais.length - 1] === eu) primeiro?.focus();
    const tecla = (e: KeyboardEvent) => {
      if (pilhaModais[pilhaModais.length - 1] !== eu) return;
      if (e.key === 'Escape') aoFechar();
      if (e.key === 'Tab' && ref.current) {
        const focaveis = [...ref.current.querySelectorAll<HTMLElement>('input, select, textarea, button, a[href]')].filter((x) => !x.hasAttribute('disabled'));
        if (focaveis.length === 0) return;
        const [p, u] = [focaveis[0], focaveis[focaveis.length - 1]];
        if (e.shiftKey && document.activeElement === p) (e.preventDefault(), u.focus());
        else if (!e.shiftKey && document.activeElement === u) (e.preventDefault(), p.focus());
      }
    };
    window.addEventListener('keydown', tecla);
    return () => {
      window.removeEventListener('keydown', tecla);
      anterior?.focus();
    };
  }, [aoFechar, eu]);
  return (
    <div className={`modal-fundo${sobreposto ? ' modal-sobreposto' : ''}`} onMouseDown={(e) => e.target === e.currentTarget && aoFechar()}>
      {/* P3: em telas baixas (~700 px) só o corpo rola; cabeçalho e botões ficam sempre visíveis */}
      <div className={`modal modal-rodape-fixo${largo ? ' modal-largo' : medio ? ' modal-medio' : ''}`} role="dialog" aria-modal="true" aria-label={titulo} ref={ref}>
        <header>
          <h2>{titulo}</h2>
        </header>
        <div className="modal-corpo">{children}</div>
        <div className="acoes-form rodape-modal" data-teste="rodape-modal">{rodape}</div>
      </div>
    </div>
  );
}

/* ---------- Abas ---------- */
export function Abas<T extends string>({ abas, atual, aoTrocar, rotulo }: { abas: { id: T; rotulo: string; desabilitada?: string }[]; atual: T; aoTrocar: (a: T) => void; rotulo: string }) {
  return (
    <div className="abas" role="tablist" aria-label={rotulo}>
      {abas.map((a) => (
        <button key={a.id} type="button" role="tab" aria-selected={a.id === atual} className={a.id === atual ? 'ativa' : ''} disabled={Boolean(a.desabilitada)} title={a.desabilitada} onClick={() => aoTrocar(a.id)}>
          {a.rotulo}
          {a.desabilitada && <small className="em-breve"> em breve</small>}
        </button>
      ))}
    </div>
  );
}

export function Nota({ children }: { children: ReactNode }) {
  return <p className="nota">{children}</p>;
}

/* ---------- Busca com contagem e "Limpar busca" ---------- */
export function CampoBusca({ valor, setValor, rotulo, ajuda, dica, teste, total }: { valor: string; setValor: (v: string) => void; rotulo: string; ajuda: string; /** texto completo do que a busca encontra (dica ao passar o mouse) */ dica?: string; teste: string; total?: number | null }) {
  return (
    <div className="campo-busca" data-teste={`${teste}-caixa`}>
      <label>
        {rotulo}
        <input type="search" value={valor} onChange={(e) => setValor(e.target.value)} placeholder={ajuda} title={dica ?? ajuda} data-teste={teste} />
      </label>
      {valor.trim() && (
        <>
          <span className="texto-secundario" role="status" data-teste={`${teste}-contagem`}>
            {total ?? 0} resultado(s)
          </span>
          <button type="button" className="botao-secundario pequeno" onClick={() => setValor('')} data-teste={`${teste}-limpar`}>
            Limpar busca
          </button>
        </>
      )}
    </div>
  );
}

/* ---------- Resumo de indicadores: componente ÚNICO para todos os cards de resumo/KPI ----------
 * Fase 1 (C19): cartão com ícone · título (até 2 linhas, altura fixa) · valor inteiro · complemento (1 linha).
 * A grade se adapta à largura disponível (menu expandido ou recolhido): todos em linha só quando cabem
 * inteiros; senão 3 × 2 (ou 3 + 2, 2 × 2), sem cortar valores. */
export interface Indicador {
  rotulo: string;
  valor: ReactNode;
  detalhe?: ReactNode;
  teste?: string;
  icone?: NomeIcone;
  /** "?" ao lado do rótulo (texto explicativo fora do card) */
  ajuda?: ReactNode;
}
const CORES = ['az', 'vd', 'am', 'rx', 'lj', 'tq'] as const;
function iconePadrao(rotulo: string): NomeIcone {
  const r = rotulo.toLowerCase();
  if (/venc|atras|bloque|pend|aten/.test(r)) return 'alerta';
  if (/ganh|convers|sucesso/.test(r)) return 'trofeu';
  if (/fatur/.test(r)) return 'fatura';
  if (/entreg|frete/.test(r)) return 'caminhao';
  if (/dia|prazo|tempo|ciclo/.test(r)) return 'relogio';
  if (/margem|%|percent/.test(r)) return 'pizza';
  if (/valor|r\$|total em|receita|saldo|custo|pre[çc]o/.test(r)) return 'dinheiro';
  if (/client|contato|grupo/.test(r)) return 'grupo';
  if (/empresa|entidade|cnpj/.test(r)) return 'predio';
  if (/pedido|ordem|propost|document/.test(r)) return 'documento';
  return 'grafico';
}
function Faixa({ itens, classe, teste }: { itens: Indicador[]; classe: string; teste?: string }) {
  return (
    <div className="resumo-indicadores-corte">
      <dl className={`resumo-indicadores ${classe}`} data-teste={teste} data-n={itens.length}>
        {itens.map((x, i) => (
          <div key={x.rotulo} className="indicador" data-teste={x.teste}>
            {classe === 'principais' && (
              <span className={`indicador-icone c-${CORES[i % CORES.length]}`} aria-hidden="true">
                <Icone nome={x.icone ?? iconePadrao(x.rotulo)} />
              </span>
            )}
            <dt>
              {x.rotulo}
              {x.ajuda}
            </dt>
            <dd className="indicador-valor">{x.valor}</dd>
            {x.detalhe !== undefined && x.detalhe !== null && x.detalhe !== '' ? <dd className="indicador-detalhe" title={typeof x.detalhe === 'string' ? x.detalhe : undefined}>{x.detalhe}</dd> : classe === 'principais' && <dd className="indicador-detalhe" aria-hidden="true" />}
          </div>
        ))}
      </dl>
    </div>
  );
}

export function ResumoIndicadores({ itens, secundarios, teste, compacto }: { itens: Indicador[]; secundarios?: Indicador[]; teste?: string; compacto?: boolean }) {
  return (
    <div className={`bloco-indicadores${compacto ? ' compacto' : ''}`} data-teste={teste}>
      <Faixa itens={itens} classe="principais" />
      {secundarios && secundarios.length > 0 && <Faixa itens={secundarios} classe="secundarios" teste={teste ? `${teste}-secundarios` : undefined} />}
    </div>
  );
}

/**
 * P4 bloco 3 (I04, fonte B aprovada em 17/10/2026): card de resumo (listagens de Pedidos/Ordens, fichas e conferências).
 * Rótulo, valor e auxiliar ocupam linhas compartilhadas (subgrid em .cards-lista): o valor fica na mesma linha em todos
 * os cards, mesmo com rótulo de várias linhas, e nunca é cortado nem reduzido para caber.
 */
export function CardLista({ icone, tom, rotulo, valor, detalhe, ajuda, teste }: { icone: NomeIcone; tom: string; rotulo: ReactNode; valor: ReactNode; detalhe?: ReactNode; ajuda?: ReactNode; teste: string }) {
  return (
    <div className={`card-lista tom-${tom}`} data-teste={teste}>
      <span className="icone-card" aria-hidden="true">
        <Icone nome={icone} />
      </span>
      <div className="corpo-card">
        <span className="rotulo-card-lista">
          <span>{rotulo}</span>
          {ajuda}
        </span>
        <strong className="valor-card-lista" data-teste={`${teste}-valor`}>
          {valor}
        </strong>
        <span className="detalhe-card-lista">{detalhe ?? ' '}</span>
      </div>
    </div>
  );
}
/** Grupo de cards de resumo; o número de colunas se ajusta à largura disponível do próprio grupo. */
export function CardsLista({ n, children, teste }: { n: number; children: ReactNode; teste?: string }) {
  return (
    <div className="cards-lista-grupo">
      <div className={`cards-lista n${n}`} data-teste={teste}>
        {children}
      </div>
    </div>
  );
}
/** P4 bloco 3 (I07/I09): chips de Status com o nome e depois a quantidade, inclusive zero (Pedidos e Ordens). */
export function ChipsStatus({ itens, teste }: { itens: { nome: string; n: number; cor: string; titulo?: string }[]; teste?: string }) {
  return (
    <ul className="status-pedidos" data-teste={teste} aria-label="Status">
      <li className="titulo-status">Status</li>
      {itens.map((s) => (
        <li key={s.nome} className={`chip-status st-${s.cor}${s.n ? '' : ' zero'}`} title={s.titulo} data-teste={`chip-status-${s.nome}`}>
          <i aria-hidden="true" />
          {s.nome} <b>{s.n}</b>
        </li>
      ))}
    </ul>
  );
}

/* ---------- Carga de dados da API: carregando / erro / dados ---------- */
export function useCarga<T>(buscar: () => Promise<T>, deps: unknown[]) {
  const [dados, setDados] = useState<T | null>(null);
  const [erro, setErro] = useState<string | null>(null);
  // Lixeira: link direto para registro na Lixeira (404 na_lixeira) — EstadoCarga mostra o aviso e o atalho
  const [lixeira, setLixeira] = useState<NaLixeira | null>(null);
  const [carregando, setCarregando] = useState(true);
  // `deps` decide quando buscar de novo (mesma semântica do useEffect).
  const carregar = useCallback(buscar, deps);
  // Só a resposta da ÚLTIMA busca vale: uma resposta antiga que chega depois (ex.: a lista completa, após
  // digitar na busca) é descartada, para não sobrescrever o resultado atual.
  const ultima = useRef(0);
  const recarregar = useCallback(async () => {
    const minha = ++ultima.current;
    setCarregando(true);
    setErro(null);
    setLixeira(null);
    try {
      const r = await carregar();
      if (minha === ultima.current) setDados(r);
    } catch (e) {
      if (minha === ultima.current) {
        setErro((e as Error).message || 'Não foi possível carregar.');
        if (e instanceof ErroApi && e.codigo === 'na_lixeira') setLixeira(e.detalhes as NaLixeira);
      }
    } finally {
      if (minha === ultima.current) setCarregando(false);
    }
  }, [carregar]);
  useEffect(() => {
    void recarregar();
  }, [recarregar]);
  return { dados, setDados, erro, lixeira, carregando, recarregar };
}

/** Registro na Lixeira aberto por link direto (só para quem tem "Ver na Lixeira"; os demais recebem "não encontrado"). */
export interface NaLixeira {
  lixeiraId: string;
  entidade: string;
  numero: string;
}

export function AvisoNaLixeira({ lixeira, teste }: { lixeira: NaLixeira; teste?: string }) {
  return (
    <div className="aviso aviso-alerta" role="alert" data-teste={teste ? `${teste}-na-lixeira` : 'na-lixeira'}>
      <span className="rotulo-nivel">▲ Na Lixeira</span>
      <strong>{lixeira.numero}</strong> está na Lixeira: não pode ser aberto nem alterado enquanto estiver lá.{' '}
      <a href={`#/lixeira?registro=${lixeira.lixeiraId}`}>Ver na Lixeira</a>
    </div>
  );
}

/** Estado da tela enquanto carrega ou se a carga falhou (com "Tentar de novo"). */
export function EstadoCarga({ carregando, erro, recarregar, teste, lixeira }: { carregando: boolean; erro: string | null; recarregar: () => void; teste?: string; lixeira?: NaLixeira | null }) {
  if (lixeira) return <AvisoNaLixeira lixeira={lixeira} teste={teste} />;
  if (erro)
    return (
      <div className="aviso aviso-erro" role="alert" data-teste={teste ? `${teste}-erro` : undefined}>
        {erro}{' '}
        <button type="button" className="botao-secundario pequeno" onClick={recarregar}>
          Tentar de novo
        </button>
      </div>
    );
  if (carregando)
    return (
      <p className="carregando-tela" role="status" data-teste={teste ? `${teste}-carregando` : undefined}>
        <span className="girando" aria-hidden /> Carregando…
      </p>
    );
  return null;
}

/* ---------- Estados de tela e de ação (DEMO-03, telas/Estado.tsx): a mensagem de bloqueio diz EXATAMENTE o que falta ---------- */
export type TipoEstado = 'vazio' | 'carregando' | 'erro' | 'bloqueado' | 'concluido' | 'parcial';
const ICONE: Record<TipoEstado, string> = { vazio: '○', carregando: '…', erro: '✖', bloqueado: '⛔', concluido: '✓', parcial: '◐' };
const NOME: Record<TipoEstado, string> = { vazio: 'Nada por aqui', carregando: 'Carregando', erro: 'Erro', bloqueado: 'Bloqueado', concluido: 'Concluído', parcial: 'Parcialmente concluído' };

export function Estado({ tipo, titulo, children, itens, teste }: { tipo: TipoEstado; titulo?: string; children?: ReactNode; itens?: string[]; teste?: string }) {
  return (
    <div className={`estado estado-${tipo}`} role={tipo === 'erro' || tipo === 'bloqueado' ? 'alert' : 'status'} aria-busy={tipo === 'carregando' || undefined} data-teste={teste} data-estado={tipo}>
      <p className="estado-titulo">
        <span className="estado-icone" aria-hidden>
          {tipo === 'carregando' ? <span className="girando" /> : ICONE[tipo]}
        </span>{' '}
        <span className="estado-nome">{NOME[tipo]}</span>
        {titulo && <span>: {titulo}</span>}
      </p>
      {itens && itens.length > 0 && (
        <ul className="estado-itens">
          {itens.map((x) => (
            <li key={x}>{x}</li>
          ))}
        </ul>
      )}
      {children}
    </div>
  );
}

/* ---------- Ajuda "?" de campos complexos (DEMO-03, telas/Ajuda.tsx): mouse, foco, clique e toque; Esc fecha ---------- */
export function Ajuda({ titulo, children }: { titulo: string; children: string }) {
  const id = useId();
  const [fixo, setFixo] = useState(false);
  const [sobre, setSobre] = useState(false);
  const aberto = fixo || sobre;
  return (
    // P3-05: o "?" fica colado ao fim da frase (U+2060 impede a quebra entre a última palavra e o ícone)
    <span className="ajuda-campo" onMouseEnter={() => setSobre(true)} onMouseLeave={() => setSobre(false)}>
      {'\u2060'}
      <button
        type="button"
        className="botao-ajuda"
        aria-label={`Ajuda: ${titulo}`}
        aria-expanded={aberto}
        aria-describedby={aberto ? id : undefined}
        onClick={(e) => {
          e.preventDefault();
          // clique/toque FIXA o balão; só um segundo clique (já fixo) fecha — aberto só pelo hover, o clique não fecha
          if (fixo) {
            setFixo(false);
            setSobre(false);
          } else setFixo(true);
        }}
        onFocus={() => setSobre(true)}
        onBlur={() => {
          setSobre(false);
          setFixo(false);
        }}
        onKeyDown={(e) => {
          if (e.key === 'Escape' && aberto) {
            e.stopPropagation();
            setFixo(false);
            setSobre(false);
          }
        }}
        data-teste={`ajuda-${titulo}`}
      >
        ?
      </button>
      {aberto && (
        <span role="tooltip" id={id} className="balao-ajuda">
          <strong>{titulo}:</strong> {children}
        </span>
      )}
    </span>
  );
}

/* ---------- Tabelas (Fase 1, F1-04): ordenação pelo cabeçalho e colunas redimensionáveis ----------
 * Clicar no título ordena; clicar de novo inverte (▲ crescente, ▼ decrescente; aria-sort). A alça à direita de cada
 * título ajusta a largura (arrastar, ou setas ← → com foco nela); a largura vale enquanto a tela estiver aberta. */
export type Direcao = 'asc' | 'desc';
export interface Ordenacao<K extends string> {
  coluna: K;
  direcao: Direcao;
  alternar: (c: K, padrao?: Direcao) => void;
}
type Valor = string | number | null | undefined;
export function useOrdenacao<K extends string>(coluna: K, direcao: Direcao = 'asc') {
  const [o, setO] = useState<{ coluna: K; direcao: Direcao }>({ coluna, direcao });
  const alternar = useCallback((c: K, padrao: Direcao = 'asc') => setO((x) => (x.coluna === c ? { coluna: c, direcao: x.direcao === 'asc' ? 'desc' : 'asc' } : { coluna: c, direcao: padrao })), []);
  /** Cópia ordenada; vazios sempre por último; desempate estável pela função dada. */
  const ordenar = <T,>(lista: T[], chaves: Partial<Record<K, (t: T) => Valor>>, desempate?: (a: T, b: T) => number): T[] => {
    const f = chaves[o.coluna];
    if (!f) return lista;
    const sinal = o.direcao === 'asc' ? 1 : -1;
    return lista.slice().sort((a, b) => {
      const [x, y] = [f(a), f(b)];
      const vx = x === null || x === undefined || x === '';
      const vy = y === null || y === undefined || y === '';
      if (vx || vy) return vx && vy ? (desempate?.(a, b) ?? 0) : vx ? 1 : -1;
      const c = typeof x === 'number' && typeof y === 'number' ? x - y : String(x).localeCompare(String(y), 'pt-BR', { numeric: true, sensitivity: 'base' });
      return c !== 0 ? c * sinal : (desempate?.(a, b) ?? 0);
    });
  };
  /** P2-04: aplicar a ordenação de uma visão salva */
  const definir = useCallback((c: K, d: Direcao) => setO({ coluna: c, direcao: d }), []);
  return { ...o, alternar, ordenar, definir };
}

export function Th<K extends string>({ children, ord, coluna, padrao, numero, className, teste, titulo }: { children: ReactNode; ord?: Ordenacao<K>; coluna?: K; padrao?: Direcao; numero?: boolean; className?: string; teste?: string; titulo?: string }) {
  const ref = useRef<HTMLTableCellElement>(null);
  const ativo = Boolean(ord && coluna && ord.coluna === coluna);
  const larguraInicial = () => ref.current?.getBoundingClientRect().width ?? 0;
  const definir = (w: number) => {
    if (!ref.current) return;
    const px = `${Math.max(56, Math.round(w))}px`;
    ref.current.style.width = px;
    ref.current.style.minWidth = px;
    ref.current.style.maxWidth = px;
  };
  const arrastar = (e: EventoPonteiro) => {
    e.preventDefault();
    e.stopPropagation();
    const [x0, w0] = [e.clientX, larguraInicial()];
    const mover = (ev: PointerEvent) => definir(w0 + ev.clientX - x0);
    const soltar = () => (window.removeEventListener('pointermove', mover), window.removeEventListener('pointerup', soltar), document.body.classList.remove('redimensionando'));
    document.body.classList.add('redimensionando');
    window.addEventListener('pointermove', mover);
    window.addEventListener('pointerup', soltar);
  };
  const classes = [className, numero ? 'numero' : '', ativo ? 'ordenada' : ''].filter(Boolean).join(' ') || undefined;
  return (
    <th ref={ref} className={classes} aria-sort={ativo ? (ord!.direcao === 'asc' ? 'ascending' : 'descending') : undefined} data-teste={teste} title={titulo}>
      {ord && coluna ? (
        <button type="button" className="ordenar-coluna" onClick={() => ord.alternar(coluna, padrao)} data-teste={teste ? `${teste}-ordenar` : undefined}>
          <span className="rotulo-coluna">{children}</span>
          <span className="seta-ordem" data-direcao={ativo ? ord.direcao : 'nenhuma'} aria-hidden="true" />
        </button>
      ) : (
        <span className="rotulo-coluna">{children}</span>
      )}
      <span
        className="alca-coluna"
        role="separator"
        aria-orientation="vertical"
        aria-label="Ajustar largura da coluna"
        tabIndex={0}
        onPointerDown={arrastar}
        onKeyDown={(e) => {
          if (e.key === 'ArrowLeft' || e.key === 'ArrowRight') (e.preventDefault(), definir(larguraInicial() + (e.key === 'ArrowRight' ? 16 : -16)));
        }}
      />
    </th>
  );
}

/* ---------- Rolagem horizontal com indicação de mais colunas (P3) ----------
 * A tabela continua rolando dentro do próprio quadro (fonte e dados inalterados); quando há colunas além da borda
 * direita, aparecem o aviso "Mais colunas à direita" e uma sombra na borda, que somem ao chegar ao fim. */
export function RolagemIndicada({ className, children, teste }: { className: string; children: ReactNode; teste?: string }) {
  const ref = useRef<HTMLDivElement>(null);
  const [mais, setMais] = useState(false);
  const medir = useCallback(() => {
    const el = ref.current;
    if (el) setMais(el.scrollWidth - el.clientWidth - el.scrollLeft > 2);
  }, []);
  useEffect(() => {
    medir();
    const el = ref.current;
    if (!el || typeof ResizeObserver === 'undefined') return;
    const ro = new ResizeObserver(medir);
    ro.observe(el);
    if (el.firstElementChild) ro.observe(el.firstElementChild);
    return () => ro.disconnect();
  }, [medir]);
  return (
    <>
      {mais && (
        <p className="aviso-rolagem" data-teste={teste ? `${teste}-mais-colunas` : 'mais-colunas'}>
          Mais colunas à direita → role o quadro
        </p>
      )}
      <div className={`rolagem-indicada${mais ? ' ha-mais' : ''}`}>
        <div ref={ref} className={className} onScroll={medir}>
          {children}
        </div>
      </div>
    </>
  );
}
